import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { industries } from "@/data/industries";
import { CarouselPagination } from "./CarouselPagination";
import { useState, useEffect } from "react";

const IndustryCard = ({
  slug,
  title,
  benefit,
  bullets,
  roi,
  img,
  alt,
}: (typeof industries)[number]) => (
  <Card className="overflow-hidden rounded-md shadow-sm flex flex-col h-full">
    <img src={img} alt={alt} loading="lazy" className="h-40 w-full object-cover" />
    <CardHeader className="p-4">
      <CardTitle className="text-xl">{title}</CardTitle>
      <p className="text-base text-muted-foreground">{benefit}</p>
    </CardHeader>
    <CardContent className="p-4 pt-0 flex-1 flex flex-col justify-between">
      <div>
        <ul className="list-disc pl-5 space-y-2 text-base text-muted-foreground">
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
        <p className="mt-3 text-base text-muted-foreground">{roi}</p>
      </div>
      <Button variant="outline" className="mt-4 w-full" asChild>
        <Link to={`/industries/${slug}`} aria-label={`Learn more about ${title} automation solutions`}>
          Learn more about {title}
        </Link>
      </Button>
    </CardContent>
  </Card>
);

const Industries = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollToSlide = (index: number) => {
    if (api) {
      api.scrollTo(index);
    }
  };

  return (
    <section id="industries" className="py-16 md:py-24 bg-[hsl(var(--section-subtle))]">
      <div className="container mx-auto">
        <header className="mb-10 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold">Industries We Help</h2>
          <p className="text-lg md:text-xl text-muted-foreground mt-3">
            AI Montclair will superpower your business. Explore industries already benefiting.
          </p>
        </header>

        {/* Mobile: enhanced carousel with peek */}
        <div className="md:hidden">
          <Carousel 
            opts={{ align: "start", loop: false }}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent className="-ml-2">
              {industries.map((ind, index) => (
                <CarouselItem 
                  key={ind.slug} 
                  className="pl-2 basis-[85%]"
                  role="tabpanel"
                  aria-label={`Slide ${index + 1} of ${industries.length}: ${ind.title}`}
                >
                  <IndustryCard {...ind} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
          
          {/* Pagination */}
          <CarouselPagination 
            currentSlide={current}
            totalSlides={industries.length}
            onDotClick={scrollToSlide}
          />
          
          {/* See all industries link */}
          <div className="text-center mt-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/industries" className="text-muted-foreground hover:text-foreground">
                See all industries →
              </Link>
            </Button>
          </div>
        </div>

        {/* Desktop: 4-column grid with 32px gaps */}
        <div className="hidden md:grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {industries.map((ind) => (
            <IndustryCard key={ind.slug} {...ind} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;
